import { parse } from "query-string";
import React from "react";
import { createPortal } from "react-dom";

import { isMqDesktop, isMqMobile } from "@/mk/common/responsive";

/**
 * Floating diagnostic overlay for debugging the responsive/device-mode mismatch
 * (desktop CSS + mobile React layout, or vice versa) that breaks the PostDetail
 * layout on phones in "Desktop site" mode.
 *
 * Enable by adding `?diag=1` to any forum PostDetail URL. Renders read-only info
 * in the top-left corner. Safe to leave in prod: hidden unless the param is present.
 */

// Exact media strings compiled from static/css/common/mixins/responsive.scss.
// Prod always builds with RESPONSIVE_BASED_ON_VIEWPORT=false → device-based branch.
const primitives: Array<[string, string]> = [
    ["min-width:979.98", "(min-width:979.98px)"],
    ["max-width:979.98", "(max-width:979.98px)"],
    ["min-device-width:600", "(min-device-width:600px)"],
    ["max-device-width:599", "(max-device-width:599px)"],
    ["min-device-height:600", "(min-device-height:600px)"],
    ["max-device-height:599", "(max-device-height:599px)"],
];

interface Props {
    reactIsMobile: boolean;
    search: string;
}

interface State {
    // Portal is client-only: stays false through SSR + first hydration render
    // (matching the SSR null output), flips true in componentDidMount. This
    // avoids a hydration mismatch from createPortal(document.body).
    mounted: boolean;
    closed: boolean;
    tick: number;
}

export class PostDetailDiagnostics extends React.PureComponent<Props, State> {
    private intervalId: number | null = null;

    public state: State = { mounted: false, closed: false, tick: 0 };

    private get enabled() {
        return typeof window !== "undefined" && !!parse(this.props.search).diag;
    }

    public componentDidMount() {
        if (!this.enabled) {
            return;
        }
        this.setState({ mounted: true });
        this.intervalId = window.setInterval(this.refresh, 500);
        window.addEventListener("resize", this.refresh);
        window.addEventListener("orientationchange", this.refresh);
    }

    public componentWillUnmount() {
        if (this.intervalId !== null) {
            window.clearInterval(this.intervalId);
        }
        window.removeEventListener("resize", this.refresh);
        window.removeEventListener("orientationchange", this.refresh);
    }

    private refresh = () => {
        this.setState((s) => ({ tick: s.tick + 1 }));
    };

    private mm(query: string) {
        return window.matchMedia(query).matches;
    }

    // Binary-search the exact CSS px value the media queries evaluate against.
    // This is the real (often fractional under zoom/high-DPR) viewport size that
    // responsive breakpoints actually compare — it can differ from
    // window.innerWidth on phones in "Desktop site" mode, which is precisely how
    // a value between two integer breakpoints slips through.
    private measureMq(feature: "width" | "height"): number {
        let lo = 0;
        let hi = 20000;
        for (let i = 0; i < 44; i++) {
            const mid = (lo + hi) / 2;
            if (window.matchMedia(`(min-${feature}: ${mid}px)`).matches) {
                lo = mid;
            } else {
                hi = mid;
            }
        }
        return lo;
    }

    public render() {
        if (!this.enabled || !this.state.mounted || this.state.closed) {
            return null;
        }

        const { reactIsMobile } = this.props;

        const jsMobile = isMqMobile();
        const jsDesktop = isMqDesktop();

        // Main post column width — the element whose CSS max-width flips at the
        // 979.98px breakpoint (624px capped on desktop, 100vw fluid on mobile).
        let mainW: string = "?";
        const mc = document.querySelector('[class*="mainContent"]') as HTMLElement | null;
        if (mc) {
            mainW = `${Math.round(mc.getBoundingClientRect().width)}px`;
        }

        // Exact width/height the media queries actually compare (may be fractional).
        const mqW = this.measureMq("width");
        const mqH = this.measureMq("height");
        const mqWfractional = Math.abs(mqW - Math.round(mqW)) > 0.001;
        const vv = window.visualViewport;

        // The actionable bug: redux device flag disagrees with live matchMedia,
        // or no responsive mode resolves cleanly.
        const reduxVsLive = reactIsMobile !== jsMobile;
        const modeBroken = jsMobile === jsDesktop; // both true or both false
        const hasMismatch = reduxVsLive || modeBroken;

        const row = (label: string, value: string, warn = false) => (
            <div key={label} style={styles.row}>
                <span style={styles.label}>{label}</span>
                <span style={{ ...styles.value, ...(warn ? styles.warn : {}) }}>{value}</span>
            </div>
        );

        return createPortal(
            <div style={styles.panel}>
                <div style={styles.header}>
                    <span>Diagnostics</span>
                    <span
                        style={styles.close}
                        onClick={() => this.setState({ closed: true })}
                        role="button"
                    >
                        ✕
                    </span>
                </div>

                {hasMismatch && (
                    <div style={styles.alert}>
                        {reduxVsLive
                            ? "MISMATCH: redux isMobile ≠ live matchMedia"
                            : "MISMATCH: no responsive mode resolved"}
                    </div>
                )}

                {row("viewport (innerW×H)", `${window.innerWidth} × ${window.innerHeight}`)}
                {row("mq width (exact)", `${mqW.toFixed(2)}px`, mqWfractional)}
                {row("mq height (exact)", `${mqH.toFixed(2)}px`)}
                {row(
                    "visualViewport",
                    vv
                        ? `${vv.width.toFixed(1)} × ${vv.height.toFixed(1)} @${vv.scale.toFixed(3)}`
                        : "n/a",
                )}
                {row("screen", `${window.screen.width} × ${window.screen.height}`)}
                {row("devicePixelRatio", String(window.devicePixelRatio))}
                {row("doc scrollWidth", String(document.documentElement.scrollWidth))}
                {row("main column", mainW)}

                <div style={styles.sep}>media primitives</div>
                {primitives.map(([label, query]) => row(label, String(this.mm(query))))}

                <div style={styles.sep}>resolved mode</div>
                {row("JS isMqDesktop()", String(jsDesktop), modeBroken)}
                {row("JS isMqMobile()", String(jsMobile), modeBroken)}
                {row("React isMobile (redux)", String(reactIsMobile), reduxVsLive)}

                <div style={styles.sep}>userAgent</div>
                <div style={styles.ua}>{window.navigator.userAgent}</div>
            </div>,
            document.body,
        );
    }
}

const styles: { [k: string]: React.CSSProperties } = {
    panel: {
        position: "fixed",
        top: 8,
        left: 8,
        zIndex: 2147483647,
        maxWidth: 340,
        maxHeight: "90vh",
        overflowY: "auto",
        background: "rgba(10, 10, 20, 0.92)",
        color: "#e8e8e8",
        font: "11px/1.4 monospace",
        padding: "6px 8px",
        borderRadius: 6,
        boxShadow: "0 2px 12px rgba(0,0,0,0.5)",
        pointerEvents: "auto",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontWeight: "bold",
        marginBottom: 4,
        color: "#fff",
    },
    close: { cursor: "pointer", padding: "0 4px" },
    alert: {
        background: "#c0392b",
        color: "#fff",
        padding: "3px 5px",
        borderRadius: 3,
        marginBottom: 5,
        fontWeight: "bold",
    },
    row: { display: "flex", justifyContent: "space-between", gap: 8 },
    label: { color: "#9fb4c8", whiteSpace: "nowrap" },
    value: { color: "#fff", textAlign: "right", wordBreak: "break-all" },
    warn: { color: "#ffde59", fontWeight: "bold" },
    sep: {
        marginTop: 5,
        marginBottom: 2,
        color: "#7f8c9b",
        borderTop: "1px solid rgba(255,255,255,0.15)",
        paddingTop: 3,
    },
    ua: { color: "#c8c8c8", wordBreak: "break-all" },
};
